import type { ReactNode } from 'react';

import type { PanelKeys } from './types';

/**
 * Global registry of panel components.
 *
 * This is a temporary solution to avoid circular dependencies between
 * @databases/layout (PanelRenderer) and @databases/frontend (panel implementations).
 *
 * Panels are registered as React.ReactNode (typically lazy components wrapped in Suspense).
 */
const registry = new Map<PanelKeys, ReactNode>();

/**
 * Registers a panel component in the global registry.
 *
 * @param key - The unique key identifying the panel type
 * @param component - The React node to render (typically a lazy component wrapped in Suspense)
 *
 * @example
 * ```tsx
 * const FieldSettingsPanel = lazy(() => import('./FieldSettingsPanel'));
 *
 * registerPanelForPanelRenderer('FieldSettingsPanel', (
 *   <Suspense>
 *     <FieldSettingsPanel />
 *   </Suspense>
 * ));
 * ```
 */
export function registerPanelForPanelRenderer(
  key: PanelKeys,
  component: ReactNode
): void {
  registry.set(key, component);
}

/**
 * Retrieves all registered panel components as a record.
 * This should ONLY be used by the PanelRenderer.
 *
 * @returns A record mapping panel keys to their React nodes
 */
export function getAllPanelsForPanelRenderer(): Record<PanelKeys, ReactNode> {
  return Object.fromEntries(registry) as Record<PanelKeys, ReactNode>;
}
